import * as React from 'react';
import { Lock, Send } from 'lucide-react';
import { SlideOver, Avatar, Button, useToast } from '@/components/ui';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { api } from '@/lib/api-client';
import { sanitizeHtml } from '@/lib/sanitize-html';
import { formatDateTime } from '@/lib/date-format';
import { useI18n } from '@/lib/i18n';

interface ThreadMessageItem {
  id: string;
  body: string;
  createdAt: string;
  user: { id: string; firstName: string; lastName: string } | null;
}

interface ThreadData {
  id: string | null;
  startedBy: { id: string; firstName: string; lastName: string } | null;
  createdAt: string | null;
  messages: ThreadMessageItem[];
}

interface ThreadDrawerProps {
  ticketId: string;
  conversationId: string | null;
  onClose: () => void;
  timezone?: string;
}

/**
 * Private-thread side panel anchored to one conversation entry (Freshdesk-
 * style "Start a thread" drawer): thread header, message list, and its own
 * composer — separate from the main ticket conversation timeline.
 */
export function ThreadDrawer({ ticketId, conversationId, onClose, timezone }: ThreadDrawerProps) {
  const { toast } = useToast();
  const { t } = useI18n();
  const [thread, setThread] = React.useState<ThreadData | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [body, setBody] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [editorKey, setEditorKey] = React.useState(0);

  React.useEffect(() => {
    if (!conversationId) { setThread(null); return; }
    setLoading(true);
    api
      .get<{ data: ThreadData }>(`/tickets/${ticketId}/conversations/${conversationId}/thread`)
      .then((res) => setThread(res.data))
      .catch(() => setThread(null))
      .finally(() => setLoading(false));
  }, [ticketId, conversationId]);

  async function sendMessage() {
    if (!conversationId || !body.trim()) return;
    setSending(true);
    try {
      const res = await api.post<{ data: ThreadMessageItem }>(
        `/tickets/${ticketId}/conversations/${conversationId}/thread/messages`,
        { body },
      );
      setThread((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          id: prev.id ?? `local-${res.data.id}`,
          startedBy: prev.startedBy ?? res.data.user,
          createdAt: prev.createdAt ?? res.data.createdAt,
          messages: [...prev.messages, res.data],
        };
      });
      setBody('');
      setEditorKey((k) => k + 1);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('tp.thread.unableToSend');
      toast({ variant: 'error', title: t('tp.thread.messageFailed'), description: message });
    } finally {
      setSending(false);
    }
  }

  const hasThreadMessages = (thread?.messages.length ?? 0) > 0;

  return (
    <SlideOver
      open={!!conversationId}
      onClose={onClose}
      widthClassName="max-w-md"
      title={(
        <span className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-warning-600" aria-hidden="true" />
          {hasThreadMessages ? t('tp.thread.privateThread') : t('tp.thread.startPrivateThread')}
        </span>
      )}
      footer={conversationId ? (
        <div className="space-y-2">
          <RichTextEditor
            key={editorKey}
            value=""
            onChange={setBody}
            placeholder={t('tp.thread.replyPlaceholder')}
            minHeight="70px"
            disabled={sending}
          />
          <div className="flex justify-end">
            <Button size="sm" disabled={sending || !body.trim()} onClick={() => void sendMessage()}>
              <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              {sending ? t('tp.common.sending') : t('tp.common.send')}
            </Button>
          </div>
        </div>
      ) : undefined}
    >
      <div className="space-y-3 p-5">
        {loading && <p className="text-sm text-ink-subtle">{t('tp.common.loading')}</p>}
        {!loading && thread && (
          <>
            {hasThreadMessages ? (
              <p className="text-xs text-ink-subtle">
                {t('tp.thread.startedBy', { name: thread.startedBy ? `${thread.startedBy.firstName} ${thread.startedBy.lastName}` : t('tp.thread.someone') })} ·{' '}
                {formatDateTime(thread.createdAt, timezone)}
              </p>
            ) : (
              <p className="text-sm text-ink-subtle">
                {t('tp.thread.empty')}
              </p>
            )}
            {thread.messages.map((message) => (
              <div key={message.id} className="flex items-start gap-2">
                <Avatar
                  name={message.user ? `${message.user.firstName} ${message.user.lastName}` : t('tp.common.system')}
                  id={message.user?.id}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium">
                      {message.user ? `${message.user.firstName} ${message.user.lastName}` : t('tp.common.system')}
                    </span>
                    <span className="text-xs text-ink-subtle">{formatDateTime(message.createdAt, timezone)}</span>
                  </div>
                  <div
                    className="ticket-rich-content prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.body) }}
                  />
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </SlideOver>
  );
}
